import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gear, Envelope, ClockCounterClockwise, DownloadSimple, Plus, Trash, PaperPlaneTilt, WarningCircle, ChatCircleDots } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { api, formatApiErrorDetail } from "../lib/api";
import { useEditConfirm } from "../context/EditConfirmContext";
import { formatET } from "../lib/dateUtils";

const navItems = [{ to: "/admin/settings", label: "Admin Settings", icon: <Gear size={18} weight="duotone" />, testId: "nav-admin-settings" }];

const emptyStep = () => ({ delay_hours: 24, channel: "email", subject: "", body: "" });
const emptySequence = () => ({ name: "", product_interest: "any", active: true, steps: [emptyStep()] });

export default function AdminSettingsPage() {
  const confirmEdit = useEditConfirm();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [digestFrequency, setDigestFrequency] = useState("weekly");
  const [leadUploadThreshold, setLeadUploadThreshold] = useState(20);
  const [lowLeadBothThreshold, setLowLeadBothThreshold] = useState(5);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [history, setHistory] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [newSeq, setNewSeq] = useState(emptySequence());
  const [showNewSeqForm, setShowNewSeqForm] = useState(false);
  const [failedAgreements, setFailedAgreements] = useState([]);
  const [resendingId, setResendingId] = useState(null);
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [sendingWhatsappTest, setSendingWhatsappTest] = useState(false);
  const [sendingNateRTest, setSendingNateRTest] = useState(false);
  const [overrides, setOverrides] = useState([]);
  const [siteBannerEnabled, setSiteBannerEnabled] = useState(false);
  const [siteBannerMessage, setSiteBannerMessage] = useState("");
  const [siteBannerDraft, setSiteBannerDraft] = useState("");
  const [siteBannerToggling, setSiteBannerToggling] = useState(false);
  const [siteBannerSaving, setSiteBannerSaving] = useState(false);
  const [deleteRepCode, setDeleteRepCode] = useState("");
  const [deleteLookup, setDeleteLookup] = useState(null);
  const [deleteLookingUp, setDeleteLookingUp] = useState(false);
  const [deletingLeads, setDeletingLeads] = useState(false);

  const toggleSiteBanner = async (enabled) => {
    if (!(await confirmEdit(`the Site-Wide Banner (turn ${enabled ? "ON" : "OFF"})`))) return;
    setSiteBannerToggling(true);
    try {
      const { data } = await api.patch("/admin/maintenance-mode", { enabled });
      setSiteBannerEnabled(data.enabled);
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSiteBannerToggling(false);
    }
  };

  const saveSiteBannerMessage = async () => {
    if (!(await confirmEdit("the Site-Wide Banner message"))) return;
    setSiteBannerSaving(true);
    try {
      const { data } = await api.patch("/admin/maintenance-mode", { message: siteBannerDraft });
      setSiteBannerMessage(data.message_text);
      toast.success("Banner message updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSiteBannerSaving(false);
    }
  };

  const fetchOverrides = () => {
    api.get("/admin/threshold-overrides").then(({ data }) => setOverrides(data)).catch(() => {});
  };

  const fetchSequences = () => {
    api.get("/admin/followup-sequences").then(({ data }) => setSequences(data));
  };

  const fetchHistory = () => {
    api.get("/admin/digest/history").then(({ data }) => setHistory(data));
  };

  const fetchFailedAgreements = () => {
    api.get("/admin/failed-agreement-emails").then(({ data }) => setFailedAgreements(data)).catch(() => {});
  };

  const fetchWhatsappStatus = () => {
    api.get("/admin/whatsapp/config").then(({ data }) => setWhatsappStatus(data)).catch(() => {});
  };

  const sendWhatsappTest = async () => {
    setSendingWhatsappTest(true);
    try {
      const { data } = await api.post("/admin/whatsapp/test", {});
      if (data.sent) {
        toast.success("Test alert sent — check your WhatsApp");
      } else if (data.mocked) {
        toast.info("Not live yet — logged instead of sent. Add the WhatsApp credentials to activate.");
      } else {
        toast.error(data.error || "Send failed");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSendingWhatsappTest(false);
    }
  };

  const sendNateRTest = async () => {
    setSendingNateRTest(true);
    try {
      const { data } = await api.post("/admin/nate-r/test-email", {});
      if (data.sent) {
        toast.success("Test email sent — check your inbox");
      } else if (data.mocked) {
        toast.info("NATE_R_RESEND_API_KEY isn't set on this environment yet — logged instead of sent.");
      } else {
        toast.error(data.error || "Send failed");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSendingNateRTest(false);
    }
  };

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => {
      setEmail(data.admin_notification_email || "");
      setPhone(data.admin_notification_phone || "");
      setDigestFrequency(data.digest_frequency || "weekly");
      setLeadUploadThreshold(data.lead_upload_threshold ?? 20);
      setLowLeadBothThreshold(data.low_lead_both_threshold ?? 5);
    });
    fetchHistory();
    fetchSequences();
    fetchFailedAgreements();
    fetchWhatsappStatus();
    fetchOverrides();
    api.get("/admin/maintenance-mode").then(({ data }) => {
      setSiteBannerEnabled(!!data.enabled);
      setSiteBannerMessage(data.message || "");
      setSiteBannerDraft(data.message || "");
    }).catch(() => {});
  }, []);

  const resendAgreementEmail = async (log) => {
    setResendingId(log.id);
    try {
      await api.post(`/admin/failed-agreement-emails/${log.id}/resend`);
      toast.success(`Resent to ${log.to}`);
      fetchFailedAgreements();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setResendingId(null);
    }
  };

  const updateNewSeqStep = (idx, field, value) => {
    setNewSeq((s) => ({ ...s, steps: s.steps.map((st, i) => (i === idx ? { ...st, [field]: value } : st)) }));
  };

  const addNewSeqStep = () => setNewSeq((s) => ({ ...s, steps: [...s.steps, emptyStep()] }));
  const removeNewSeqStep = (idx) => setNewSeq((s) => ({ ...s, steps: s.steps.filter((_, i) => i !== idx) }));

  const createSequence = async () => {
    if (!newSeq.name.trim() || newSeq.steps.length === 0) {
      toast.error("Give the sequence a name and at least one step");
      return;
    }
    try {
      await api.post("/admin/followup-sequences", newSeq);
      toast.success("Follow-up sequence created");
      setNewSeq(emptySequence());
      setShowNewSeqForm(false);
      fetchSequences();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleSequenceActive = async (seq) => {
    if (!(await confirmEdit(`the "${seq.name}" follow-up sequence (${seq.active ? "pause" : "activate"})`))) return;
    try {
      await api.patch(`/admin/followup-sequences/${seq.id}`, { ...seq, active: !seq.active });
      fetchSequences();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteSequence = async (id) => {
    try {
      await api.delete(`/admin/followup-sequences/${id}`);
      toast.success("Sequence deleted");
      fetchSequences();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const lookupRepLeadCount = async () => {
    if (!deleteRepCode.trim()) return;
    setDeleteLookingUp(true);
    setDeleteLookup(null);
    try {
      const { data } = await api.get(`/admin/reps/by-code/${deleteRepCode.trim().toUpperCase()}/lead-count`);
      setDeleteLookup(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDeleteLookingUp(false);
    }
  };

  const deleteRepLeads = async () => {
    if (!deleteLookup) return;
    if (!window.confirm(`Delete all ${deleteLookup.lead_count} lead(s) for ${deleteLookup.display_name} (${deleteLookup.rep_code})? This cannot be undone.`)) return;
    setDeletingLeads(true);
    try {
      const { data } = await api.post("/admin/reps/by-code/delete-leads", { rep_code: deleteLookup.rep_code, confirm: true });
      toast.success(data.message);
      setDeleteLookup(null);
      setDeleteRepCode("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDeletingLeads(false);
    }
  };

  const save = async () => {
    if (!(await confirmEdit("Admin Settings (notification email, phone, digest & threshold)"))) return;
    setSaving(true);
    try {
      await api.patch("/admin/settings", { admin_notification_email: email, admin_notification_phone: phone, digest_frequency: digestFrequency, lead_upload_threshold: leadUploadThreshold, low_lead_both_threshold: lowLeadBothThreshold });
      toast.success("Settings updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const { data } = await api.get("/admin/digest/preview");
      setPreviewData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmSendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/admin/digest/send-now");
      toast.success(`Weekly digest sent to ${email} for ${data.period}`);
      setPreviewOpen(false);
      fetchHistory();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSendingDigest(false);
    }
  };

  const exportHistoryCsv = () => {
    const header = "Sent At (ET),Period,Frequency,Recipient,Logins,Referrals,Messages";
    const rows = history.map((h) =>
      [formatET(h.sent_at, "MMM d, yyyy h:mm a"), h.period, h.frequency, h.recipient, h.counts.Logins, h.counts.Referrals, h.counts.Messages]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "igc-digest-history.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout navItems={navItems} moduleName="Admin Settings" moduleBackPath="/admin">
      <div data-testid="admin-settings-page" className="max-w-lg">
        <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-2" title="Portal-wide settings — notification routing, maintenance mode, follow-up sequences, and the danger-zone lead deletion tool.">Admin Settings</h1>
        <p className="text-igc-muted text-sm mb-8">All in-app alert emails and SMS notifications (new reports, referrals, client messages, module activity) route here. No emails or phone numbers are hardcoded — update either any time.</p>
        <div className="bg-igc-surface border border-igc-gold/15 p-6 space-y-6">
          <div>
            <label className="text-xs uppercase tracking-widest text-igc-muted mb-2 block">Notification Email</label>
            <Input data-testid="admin-settings-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} title="Where every system alert email (reports, referrals, client messages) is sent." className="bg-igc-surfaceLight border-igc-gold/20" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-igc-muted mb-2 block">Notification Phone (SMS)</label>
            <Input data-testid="admin-settings-phone-input" type="tel" placeholder="+1 (555) 123-4567" value={phone} onChange={(e) => setPhone(e.target.value)} title="Where WhatsApp alerts (deals, agreements, low leads, morning brief) are sent." className="bg-igc-surfaceLight border-igc-gold/20" />
          </div>
          <Button data-testid="admin-settings-save-button" onClick={save} disabled={saving} title="Saves the email, phone, digest frequency, and lead threshold settings on this page." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">{saving ? "Saving..." : "Save Settings"}</Button>
        </div>

        <div className="bg-igc-surface border border-igc-gold/15 p-6 mt-6" data-testid="admin-lead-threshold-card">
          <p className="text-igc-cream font-serif text-lg mb-1">Lead Upload Threshold</p>
          <p className="text-igc-muted text-sm mb-4">Before any lead upload (manual CSV or the automatic DataMoon pull), a rep with more than this many trust + funding leads remaining combined is skipped — they'll only receive new leads once they're at or below this number.</p>
          <label className="text-xs uppercase tracking-widest text-igc-muted mb-2 block">Threshold (leads remaining)</label>
          <Input
            data-testid="admin-settings-lead-threshold-input"
            type="number"
            min="0"
            value={leadUploadThreshold}
            onChange={(e) => setLeadUploadThreshold(parseInt(e.target.value, 10) || 0)}
            title="Reps above this many combined trust+funding leads remaining are automatically skipped on the next upload."
            className="bg-igc-surfaceLight border-igc-gold/20 max-w-[160px]"
          />
          <p className="text-igc-muted text-xs mt-2">Remember to click "Save Settings" above after changing this.</p>
        </div>

        <div className="bg-igc-surface border border-igc-gold/15 p-6 mt-6" data-testid="admin-low-lead-both-threshold-card">
          <p className="text-igc-cream font-serif text-lg mb-1">Low Lead Alert Threshold (Both Categories)</p>
          <p className="text-igc-muted text-sm mb-4">Background monitor checks every rep every 60 seconds. The moment a rep's Trust AND Funding remaining are BOTH at or below this number, Wes gets one WhatsApp listing every rep who just crossed. One category alone being low never triggers this.</p>
          <label className="text-xs uppercase tracking-widest text-igc-muted mb-2 block">Threshold (leads remaining, both categories)</label>
          <Input
            data-testid="admin-settings-low-lead-both-threshold-input"
            type="number"
            min="0"
            value={lowLeadBothThreshold}
            onChange={(e) => setLowLeadBothThreshold(parseInt(e.target.value, 10) || 0)}
            title="A rep triggers the WhatsApp alert only when BOTH trust and funding remaining are at or below this number at the same time."
            className="bg-igc-surfaceLight border-igc-gold/20 max-w-[160px]"
          />
          <p className="text-igc-muted text-xs mt-2">Remember to click "Save Settings" above after changing this.</p>
        </div>

        <div className="bg-igc-surface border border-igc-gold/15 p-6 mt-6" data-testid="admin-maintenance-mode-card">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-igc-cream font-serif text-lg mb-1">Maintenance Mode</p>
              <p className="text-igc-muted text-sm">Maintenance is now controlled per-account. Go to Users to flip any rep or manager on/off, or put everyone on/off maintenance at once — Wes's admin account can never be blocked.</p>
            </div>
            <a href="/admin/users" data-testid="admin-maintenance-mode-users-link" title="Opens the Users page where you can toggle maintenance mode per-account." className="flex-shrink-0 text-xs px-4 py-2 bg-igc-surfaceLight text-igc-goldText hover:bg-igc-surfaceLight/70 transition-colors whitespace-nowrap">
              Go to Users
            </a>
          </div>
        </div>

        <div className={`bg-igc-surface border p-6 mt-6 ${siteBannerEnabled ? "border-igc-gold/50" : "border-igc-gold/15"}`} data-testid="admin-site-banner-card">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-igc-cream font-serif text-lg mb-1">Site-Wide Banner</p>
              <p className="text-igc-muted text-sm">A notice strip across the top of the portal, visible to every logged-in user (reps, managers, clients, admin). Purely informational — never blocks anyone's login. Use the per-account toggles above for that.</p>
            </div>
            <Switch
              data-testid="admin-site-banner-toggle"
              checked={siteBannerEnabled}
              disabled={siteBannerToggling}
              onCheckedChange={toggleSiteBanner}
              title="Turns the site-wide notice banner on or off for every logged-in user."
            />
          </div>
          {siteBannerEnabled && (
            <p className="text-igc-goldText text-xs mt-3 uppercase tracking-wide" data-testid="admin-site-banner-active-notice">Site-Wide Banner is ON — everyone logged in sees the message below across the top of the portal.</p>
          )}
          <div className="mt-4 flex flex-col md:flex-row gap-3 md:items-end">
            <div className="flex-1">
              <p className="text-igc-muted text-xs uppercase tracking-widest mb-1">Banner Message</p>
              <Textarea
                data-testid="admin-site-banner-message-input"
                value={siteBannerDraft}
                onChange={(e) => setSiteBannerDraft(e.target.value)}
                rows={2}
                title="The text every logged-in user sees in the top banner."
                className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream resize-none"
              />
            </div>
            <Button
              data-testid="admin-site-banner-save-message-button"
              onClick={saveSiteBannerMessage}
              disabled={siteBannerSaving || siteBannerDraft.trim() === siteBannerMessage.trim() || !siteBannerDraft.trim()}
              title="Saves this banner message."
              className="h-10 bg-igc-gold hover:bg-igc-goldHover text-black"
            >
              {siteBannerSaving ? "Saving..." : "Save Message"}
            </Button>
          </div>
        </div>

        <div className="bg-igc-surface border border-igc-danger/30 p-6 mt-6" data-testid="admin-delete-rep-leads-card">
          <p className="text-igc-danger font-serif text-lg mb-1">Delete a Rep's Leads</p>
          <p className="text-igc-muted text-sm mb-4">New Build 4 — permanently deletes every lead currently assigned to one rep or manager (by code, e.g. VP001). Nothing else on their account is touched. This cannot be undone.</p>
          <div className="flex gap-2 mb-3">
            <Input
              data-testid="admin-delete-leads-rep-code-input"
              placeholder="Rep code (e.g. VP001)"
              value={deleteRepCode}
              onChange={(e) => { setDeleteRepCode(e.target.value); setDeleteLookup(null); }}
              title="Type the exact rep or manager code whose leads you want to permanently delete."
              className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream max-w-[220px]"
            />
            <Button
              data-testid="admin-delete-leads-lookup-button"
              onClick={lookupRepLeadCount}
              disabled={deleteLookingUp || !deleteRepCode.trim()}
              title="Looks up how many leads this rep code currently has, before you delete anything."
              className="bg-igc-surfaceLight text-igc-cream hover:bg-igc-surfaceLight/70"
            >
              {deleteLookingUp ? "Looking up..." : "Look Up"}
            </Button>
          </div>
          {deleteLookup && (
            <div className="bg-igc-surfaceLight border border-igc-danger/20 p-4 space-y-3" data-testid="admin-delete-leads-lookup-result">
              <p className="text-igc-cream text-sm">
                <span className="text-igc-goldText">{deleteLookup.display_name}</span> ({deleteLookup.rep_code}) has{" "}
                <span className="text-igc-danger font-semibold" data-testid="admin-delete-leads-count">{deleteLookup.lead_count}</span> lead{deleteLookup.lead_count === 1 ? "" : "s"} assigned.
              </p>
              {deleteLookup.lead_count > 0 && (
                <Button
                  data-testid="admin-delete-leads-confirm-button"
                  onClick={deleteRepLeads}
                  disabled={deletingLeads}
                  title="Permanently deletes every one of this rep's leads. This cannot be undone."
                  className="w-full bg-igc-danger hover:bg-igc-danger/80 text-white disabled:opacity-40"
                >
                  {deletingLeads ? "Deleting..." : `Yes, Delete All ${deleteLookup.lead_count} Lead${deleteLookup.lead_count === 1 ? "" : "s"}`}
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="bg-igc-surface border border-igc-gold/15 p-6 mt-6" data-testid="admin-threshold-override-log-card">
          <p className="text-igc-cream font-serif text-lg mb-1">Threshold Override Log</p>
          <p className="text-igc-muted text-sm mb-4">Every time an admin or manager bypassed a gate — the lead upload threshold gate or the 80% replenishment gate — for a rep. Who did it, which rep, their lead count at the time, the reason given, and when. No silent bypasses.</p>
          {overrides.length === 0 ? (
            <p className="text-igc-muted text-sm" data-testid="admin-threshold-override-log-empty">No overrides on record.</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {overrides.map((o) => (
                <div key={o.id} data-testid={`threshold-override-row-${o.id}`} className="flex items-center justify-between bg-igc-surfaceLight px-3 py-2.5 text-sm">
                  <div>
                    <p className="text-igc-cream">
                      {o.rep_display_name} <span className="text-igc-muted text-xs">({o.rep_code}, {o.category})</span>
                      <span className="ml-2 text-[9px] uppercase tracking-wide text-igc-goldText border border-igc-gold/30 px-1.5 py-0.5">{o.gate_type === "replenishment" ? "80% Replenishment" : "Lead Threshold"}</span>
                    </p>
                    <p className="text-igc-muted text-xs">By {o.overridden_by_name} ({o.overridden_by_role}) — {formatET(o.created_at, "MMM d, yyyy h:mm a")} ET</p>
                    {o.reason && <p className="text-igc-muted text-xs italic mt-0.5">"{o.reason}"</p>}
                  </div>
                  <p className="text-amber-500 text-xs whitespace-nowrap ml-3">{o.leads_remaining_at_override} leads remaining</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-igc-surface border border-igc-gold/15 p-6 mt-6" data-testid="admin-whatsapp-card">
          <p className="text-igc-cream font-serif text-lg mb-1 flex items-center gap-2">
            <ChatCircleDots size={18} weight="duotone" className="text-igc-goldText" /> WhatsApp Alerts
          </p>
          <p className="text-igc-muted text-sm mb-4">
            Real-time alerts for deals, agreements, low leads, and your morning pipeline brief — delivered straight to the Notification Phone above once IGC's WhatsApp line is connected.
          </p>
          <div className="flex items-center justify-between bg-igc-surfaceLight px-4 py-3 mb-4">
            <span className="text-sm text-igc-cream">Connection status</span>
            <span
              data-testid="admin-whatsapp-status-badge"
              className={`text-[10px] px-2 py-1 uppercase tracking-wide border ${
                whatsappStatus?.configured
                  ? "bg-igc-success/10 text-igc-success border-igc-success/30"
                  : "bg-igc-muted/10 text-igc-muted border-igc-muted/30"
              }`}
            >
              {whatsappStatus?.configured ? "Connected" : "Not Connected Yet"}
            </span>
          </div>
          <Button
            data-testid="admin-whatsapp-send-test-button"
            onClick={sendWhatsappTest}
            disabled={sendingWhatsappTest || !phone}
            title="Sends a real WhatsApp test message to your Notification Phone, to confirm the connection is live."
            className="w-full bg-igc-surfaceLight text-igc-cream hover:bg-igc-surfaceLight/70 disabled:opacity-40"
          >
            {sendingWhatsappTest ? "Sending..." : "Send Test Alert"}
          </Button>
          {!phone && <p className="text-igc-muted text-xs mt-2">Add and save a Notification Phone above first.</p>}
        </div>

        <div className="bg-igc-surface border border-igc-gold/15 p-6 mt-6" data-testid="admin-nate-r-card">
          <p className="text-igc-cream font-serif text-lg mb-1 flex items-center gap-2">
            <ChatCircleDots size={18} weight="duotone" className="text-igc-goldText" /> NATE-R Outbound Email
          </p>
          <p className="text-igc-muted text-sm mb-4">
            Sends a real test email through the NATE-R Resend key — the same key that fires Trust/Funding/Recruiting outreach sequences — to your Notification Email above, so you can confirm it's live before opening the portal.
          </p>
          <Button
            data-testid="admin-nate-r-send-test-button"
            onClick={sendNateRTest}
            disabled={sendingNateRTest || !email}
            title="Sends a real test email through the NATE-R Resend key, to confirm outbound email is working."
            className="w-full bg-igc-surfaceLight text-igc-cream hover:bg-igc-surfaceLight/70 disabled:opacity-40"
          >
            {sendingNateRTest ? "Sending..." : "Send Test Email"}
          </Button>
          {!email && <p className="text-igc-muted text-xs mt-2">Add and save a Notification Email above first.</p>}
        </div>

        <div className="bg-igc-surface border border-igc-gold/15 p-6 mt-6">
          <p className="text-igc-cream font-serif text-lg mb-1">Weekly Digest</p>
          <p className="text-igc-muted text-sm mb-4">A summary of logins, referrals, and client messages is emailed to the Notification Email above. Preview before you send:</p>
          <div className="mb-4">
            <label className="text-xs uppercase tracking-widest text-igc-muted mb-2 block">Digest Frequency</label>
            <Select value={digestFrequency} onValueChange={setDigestFrequency}>
              <SelectTrigger data-testid="admin-digest-frequency-select" title="How often the summary digest email goes out." className="w-full bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                <SelectItem value="weekly">Weekly (every Monday, 9am ET)</SelectItem>
                <SelectItem value="daily">Daily (every day, 9am ET)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-igc-muted text-xs mt-2">Remember to click "Save Settings" above after changing this.</p>
          </div>
          <Button data-testid="admin-preview-digest-button" onClick={openPreview} title="Shows exactly what the next digest email will look like, without sending it." className="w-full bg-igc-surfaceLight text-igc-cream hover:bg-igc-surfaceLight/70">
            Preview Weekly Digest
          </Button>
        </div>

        <div className="bg-igc-surface border border-igc-gold/15 p-6 mt-6" data-testid="admin-digest-history">
          <div className="flex items-center justify-between mb-1">
            <p className="text-igc-cream font-serif text-lg flex items-center gap-2">
              <ClockCounterClockwise size={18} weight="duotone" className="text-igc-goldText" /> Digest History
            </p>
            {history.length > 0 && (
              <button
                type="button"
                data-testid="admin-digest-history-export-button"
                onClick={exportHistoryCsv}
                title="Downloads every past digest email as a CSV spreadsheet."
                className="flex items-center gap-1.5 text-xs text-igc-muted hover:text-igc-goldText transition-colors"
              >
                <DownloadSimple size={14} weight="bold" /> Export CSV
              </button>
            )}
          </div>
          <p className="text-igc-muted text-sm mb-4">Every digest email that has been sent, most recent first.</p>
          {history.length === 0 ? (
            <p className="text-igc-muted text-sm" data-testid="admin-digest-history-empty">No digests sent yet.</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {history.map((h) => (
                <div key={h.id} data-testid={`digest-history-row-${h.id}`} className="flex items-center justify-between bg-igc-surfaceLight px-3 py-2.5 text-sm">
                  <div>
                    <p className="text-igc-cream">{h.period}</p>
                    <p className="text-igc-muted text-xs">{formatET(h.sent_at, "MMM d, yyyy h:mm a")} ET &middot; {h.frequency}</p>
                  </div>
                  <p className="text-igc-muted text-xs">{h.counts.Logins} logins &middot; {h.counts.Referrals} referrals &middot; {h.counts.Messages} messages</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-igc-surface border border-igc-gold/15 p-6 mt-6" data-testid="admin-followup-sequences-card">
          <div className="flex items-center justify-between mb-1">
            <p className="text-igc-cream font-serif text-lg flex items-center gap-2">
              <PaperPlaneTilt size={18} weight="duotone" className="text-igc-goldText" /> Follow-Up Sequences
            </p>
            <button data-testid="admin-followup-new-sequence-toggle" type="button" onClick={() => setShowNewSeqForm(!showNewSeqForm)} title="Opens the form to create a new automated follow-up sequence." className="flex items-center gap-1.5 text-xs text-igc-muted hover:text-igc-goldText transition-colors">
              <Plus size={14} weight="bold" /> New Sequence
            </button>
          </div>
          <p className="text-igc-muted text-sm mb-4">After a rep is assigned a lead, IGC automatically sends this sequence at the intervals you set below — by email now, with text messaging coming soon.</p>

          {sequences.length === 0 && !showNewSeqForm && <p className="text-igc-muted text-sm" data-testid="admin-followup-sequences-empty">No sequences yet — create one to auto-follow-up new leads.</p>}

          <div className="space-y-2 mb-4">
            {sequences.map((seq) => (
              <div key={seq.id} data-testid={`followup-sequence-row-${seq.id}`} className="bg-igc-surfaceLight px-3 py-2.5 text-sm flex items-center justify-between gap-3">
                <div>
                  <p className="text-igc-cream">{seq.name} <span className="text-igc-muted text-xs">({seq.product_interest}, {seq.steps.length} step{seq.steps.length !== 1 ? "s" : ""})</span></p>
                  <p className="text-igc-muted text-xs">{seq.steps.map((s) => `${s.channel} @ +${s.delay_hours}h`).join(" → ")}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button data-testid={`followup-sequence-toggle-active-${seq.id}`} onClick={() => toggleSequenceActive(seq)} title="Turns this follow-up sequence on or off without deleting it." className={`text-[10px] px-2 py-1 uppercase tracking-wide border ${seq.active ? "bg-igc-success/10 text-igc-success border-igc-success/30" : "bg-igc-muted/10 text-igc-muted border-igc-muted/30"}`}>
                    {seq.active ? "Active" : "Paused"}
                  </button>
                  <button data-testid={`followup-sequence-delete-${seq.id}`} onClick={() => deleteSequence(seq.id)} title="Permanently deletes this sequence." className="text-igc-danger hover:text-igc-danger/70"><Trash size={16} /></button>
                </div>
              </div>
            ))}
          </div>

          {showNewSeqForm && (
            <div className="border border-igc-gold/20 p-4 space-y-3" data-testid="admin-followup-new-sequence-form">
              <Input data-testid="admin-followup-new-name-input" placeholder="Sequence name" title="A short internal name for this sequence." value={newSeq.name} onChange={(e) => setNewSeq({ ...newSeq, name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
              <Select value={newSeq.product_interest} onValueChange={(v) => setNewSeq({ ...newSeq, product_interest: v })}>
                <SelectTrigger data-testid="admin-followup-new-product-select" title="Which type of lead this sequence applies to." className="w-full bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                  <SelectItem value="any">Any Lead</SelectItem>
                  <SelectItem value="funding">Funding Leads</SelectItem>
                  <SelectItem value="trust">Trust Leads</SelectItem>
                </SelectContent>
              </Select>
              {newSeq.steps.map((step, idx) => (
                <div key={idx} className="bg-igc-surfaceLight p-3 space-y-2" data-testid={`admin-followup-step-${idx}`}>
                  <div className="flex items-center gap-2">
                    <Input data-testid={`admin-followup-step-delay-${idx}`} type="number" min="0" value={step.delay_hours} onChange={(e) => updateNewSeqStep(idx, "delay_hours", parseFloat(e.target.value) || 0)} title="How many hours after lead assignment this step fires." className="w-24 bg-igc-surface border-igc-gold/20 text-igc-cream h-8 text-xs" />
                    <span className="text-igc-muted text-xs">hours after assignment, via</span>
                    <Select value={step.channel} onValueChange={(v) => updateNewSeqStep(idx, "channel", v)}>
                      <SelectTrigger data-testid={`admin-followup-step-channel-${idx}`} title="Which channel this step sends through." className="w-28 h-8 bg-igc-surface border-igc-gold/20 text-igc-cream text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="sms">SMS</SelectItem>
                      </SelectContent>
                    </Select>
                    {newSeq.steps.length > 1 && (
                      <button data-testid={`admin-followup-step-remove-${idx}`} onClick={() => removeNewSeqStep(idx)} title="Removes this step from the sequence." className="text-igc-danger hover:text-igc-danger/70 ml-auto"><Trash size={14} /></button>
                    )}
                  </div>
                  {step.channel === "email" && (
                    <Input data-testid={`admin-followup-step-subject-${idx}`} placeholder="Email subject" title="The subject line for this email step." value={step.subject} onChange={(e) => updateNewSeqStep(idx, "subject", e.target.value)} className="bg-igc-surface border-igc-gold/20 text-igc-cream h-8 text-xs" />
                  )}
                  <Textarea data-testid={`admin-followup-step-body-${idx}`} placeholder="Message body" title="The text content sent in this step." value={step.body} onChange={(e) => updateNewSeqStep(idx, "body", e.target.value)} className="bg-igc-surface border-igc-gold/20 text-igc-cream text-xs" />
                </div>
              ))}
              <div className="flex gap-2">
                <Button data-testid="admin-followup-add-step-button" onClick={addNewSeqStep} title="Adds another timed step to this sequence." className="flex-1 bg-igc-surfaceLight text-igc-cream hover:bg-igc-surfaceLight/70 text-xs h-8">+ Add Step</Button>
                <Button data-testid="admin-followup-create-button" onClick={createSequence} title="Saves this sequence and activates it for new lead assignments." className="flex-1 bg-igc-gold hover:bg-igc-goldHover text-black text-xs h-8">Create Sequence</Button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-igc-surface border border-igc-gold/15 p-6 mt-6" data-testid="admin-failed-agreements-card">
          <p className="text-igc-cream font-serif text-lg mb-1 flex items-center gap-2">
            <WarningCircle size={18} weight="duotone" className="text-igc-goldText" /> Failed Agreement Emails
          </p>
          <p className="text-igc-muted text-sm mb-4">Agreement confirmation copies that failed to send (e.g. during the Resend sandbox phase) — retry them once delivery is confirmed working.</p>
          {failedAgreements.length === 0 ? (
            <p className="text-igc-muted text-sm" data-testid="admin-failed-agreements-empty">No failed agreement emails on record.</p>
          ) : (
            <div className="space-y-2">
              {failedAgreements.map((f) => (
                <div key={f.id} data-testid={`failed-agreement-row-${f.id}`} className="bg-igc-surfaceLight px-3 py-2.5 text-sm flex items-center justify-between gap-3">
                  <div>
                    <p className="text-igc-cream">{f.signed_by || f.to} <span className="text-igc-muted text-xs">({f.code || f.to})</span> — {f.subject}</p>
                    <p className="text-igc-muted text-xs">{formatET(f.failed_at, "MMM d, yyyy h:mm a")} ET &middot; {f.error}</p>
                  </div>
                  <Button
                    data-testid={`failed-agreement-resend-button-${f.id}`}
                    onClick={() => resendAgreementEmail(f)}
                    disabled={!f.resendable || resendingId === f.id}
                    title="Tries sending this agreement confirmation email again."
                    className="flex-shrink-0 h-8 text-xs bg-igc-gold hover:bg-igc-goldHover text-black disabled:opacity-40"
                  >
                    {resendingId === f.id ? "Resending…" : "Resend"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
      >
        <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream max-w-lg max-h-[80vh] overflow-y-auto" data-testid="digest-preview-modal">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2"><Envelope size={18} weight="duotone" className="text-igc-goldText" /> Weekly Digest Preview</DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <p className="text-igc-muted text-sm" data-testid="digest-preview-loading">Building preview...</p>
          ) : previewData ? (
            <div className="space-y-4">
              <p className="text-igc-muted text-xs">{previewData.period} — will be sent to <span className="text-igc-goldText">{email || "no email configured"}</span></p>
              <p className="text-igc-muted text-xs" data-testid="digest-preview-from-address">From: <span className="text-igc-goldText">{previewData.from_address}</span></p>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-igc-surfaceLight p-3">
                  <p className="text-igc-goldText text-2xl font-serif" data-testid="digest-preview-logins">{previewData.counts.Logins}</p>
                  <p className="text-igc-muted text-[10px] uppercase tracking-widest mt-1">Logins</p>
                </div>
                <div className="bg-igc-surfaceLight p-3">
                  <p className="text-igc-goldText text-2xl font-serif" data-testid="digest-preview-referrals">{previewData.counts.Referrals}</p>
                  <p className="text-igc-muted text-[10px] uppercase tracking-widest mt-1">Referrals</p>
                </div>
                <div className="bg-igc-surfaceLight p-3">
                  <p className="text-igc-goldText text-2xl font-serif" data-testid="digest-preview-messages">{previewData.counts.Messages}</p>
                  <p className="text-igc-muted text-[10px] uppercase tracking-widest mt-1">Messages</p>
                </div>
              </div>
              <pre data-testid="digest-preview-body" className="bg-igc-surfaceLight text-igc-muted text-xs p-3 whitespace-pre-wrap max-h-48 overflow-y-auto font-sans">{previewData.body}</pre>
              <div>
                <p className="text-igc-muted text-[10px] uppercase tracking-widest mb-2">Store Report Submissions (prior week)</p>
                <div className="flex flex-wrap gap-2" data-testid="digest-preview-submission-status">
                  {previewData.submission_status.map((s) => (
                    <span
                      key={s.client_id}
                      data-testid={`digest-preview-submission-${s.client_id}`}
                      className={`text-xs px-2.5 py-1 border ${
                        s.status === "on_time"
                          ? "bg-igc-success/10 border-igc-success/30 text-igc-success"
                          : s.status === "late"
                          ? "bg-igc-gold/10 border-igc-gold/30 text-igc-goldText"
                          : "bg-igc-danger/10 border-igc-danger/30 text-igc-danger"
                      }`}
                    >
                      {s.display_name}: {s.status.replace("_", " ")}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-igc-muted text-[10px] uppercase tracking-widest mb-2">Manager Activity (past 7 days)</p>
                <div className="space-y-1" data-testid="digest-preview-manager-activity">
                  {previewData.manager_activity.length === 0 ? (
                    <p className="text-igc-muted text-xs">No managers on file.</p>
                  ) : (
                    previewData.manager_activity.map((m) => (
                      <p key={m.code} className="text-xs text-igc-cream" data-testid={`digest-preview-manager-${m.code}`}>
                        {m.display_name} ({m.code}): {m.logins} login(s), {m.new_leads} new lead(s), ${m.commission_change.toLocaleString()} in commission changes
                      </p>
                    ))
                  )}
                </div>
              </div>
              {previewData.threshold_skips && previewData.threshold_skips.length > 0 && (
                <div>
                  <p className="text-igc-muted text-[10px] uppercase tracking-widest mb-2">Reps Skipped by Lead Threshold (threshold: {previewData.lead_upload_threshold})</p>
                  <div className="space-y-1" data-testid="digest-preview-threshold-skips">
                    {previewData.threshold_skips.map((s) => (
                      <p key={s.rep_id} className="text-xs text-amber-500" data-testid={`digest-preview-threshold-skip-${s.rep_id}`}>
                        {s.rep_display_name} ({s.rep_code}): {s.leads_remaining} leads remaining — <span className="italic">{s.trend_label}</span>
                      </p>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button data-testid="digest-preview-cancel-button" onClick={() => setPreviewOpen(false)} title="Closes this preview without sending anything." className="flex-1 bg-igc-surfaceLight text-igc-cream hover:bg-igc-surfaceLight/70">
                  Cancel
                </Button>
                <Button data-testid="admin-confirm-send-digest-button" onClick={confirmSendDigest} disabled={sendingDigest} title="Sends this exact digest email right now to the Notification Email address." className="flex-1 bg-igc-gold hover:bg-igc-goldHover text-black">
                  {sendingDigest ? "Sending..." : "Send This Digest Now"}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
